import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  fetchData,
  fetchPublicSurvey,
  startPublicSurveySession,
  submitPublicSurveyAnswers,
} from "../api/client";

export const useSurveys = (page = 1, limit = 20, search = "", status = "") => {
  const queryClient = useQueryClient();

  const query = useQuery(
    ["surveys", page, limit, search, status],
    () =>
      fetchData(
        `/surveys?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}${
          status ? `&status=${encodeURIComponent(status)}` : ""
        }`
      ),
    {
      staleTime: 5000,
      refetchOnWindowFocus: false,
      select: (response) => ({
        surveys: response.data || [],
        pagination: response.pagination || { total: 0, page: 1, totalPages: 1 },
      }),
      onError: (err) => {
        console.error("Erro ao carregar pesquisas:", err.message);
      },
    }
  );

  const saveMutation = useMutation(
    (survey) => {
      const endpoint = survey.id ? `/surveys/${survey.id}` : `/surveys`;
      const method = survey.id ? "PUT" : "POST";
      return fetchData(endpoint, method, survey);
    },
    {
      onSuccess: () => queryClient.invalidateQueries("surveys"),
      onError: (error) => {
        console.error(
          "Erro ao salvar pesquisa:",
          error.response?.data?.message || error.message
        );
      },
    }
  );

  const deleteMutation = useMutation((id) => fetchData(`/surveys/${id}`, "DELETE"), {
    onSuccess: () => queryClient.invalidateQueries("surveys"),
    onError: (err) => {
      console.error("Erro ao excluir pesquisa:", err.message);
    },
  });

  const createFromTemplateMutation = useMutation(
    (payload) => fetchData("/surveys/from-template", "POST", payload),
    {
      onSuccess: () => queryClient.invalidateQueries("surveys"),
    }
  );

  const syncLegacyMutation = useMutation(
    (payload = {}) => fetchData("/surveys/legacy-sync", "POST", payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("surveys");
        queryClient.invalidateQueries("survey-analytics");
      },
    }
  );

  return {
    ...query,
    saveMutation,
    deleteMutation,
    createFromTemplateMutation,
    syncLegacyMutation,
  };
};

export const useSurveyTemplates = () =>
  useQuery(
    ["survey-templates"],
    () => fetchData("/surveys/templates", "GET"),
    {
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      select: (response) => response.data || [],
    }
  );

export const useSurveyDetails = (surveyId, enabled = true) =>
  useQuery(
    ["survey-details", surveyId],
    () => fetchData(`/surveys/${surveyId}`, "GET"),
    {
      enabled: Boolean(surveyId) && enabled,
      refetchOnWindowFocus: false,
      select: (response) => response.data || null,
    }
  );

export const useSurveyAnalytics = (surveyId, enabled = true) =>
  useQuery(
    ["survey-analytics", surveyId],
    () => fetchData(`/surveys/${surveyId}/analytics`, "GET"),
    {
      enabled: Boolean(surveyId) && enabled,
      refetchOnWindowFocus: false,
      select: (response) => response.data || null,
    }
  );

export const useSurveyResponses = (surveyId, page = 1, limit = 50, search = "", enabled = true) =>
  useQuery(
    ["survey-responses", surveyId, page, limit, search],
    () =>
      fetchData(
        `/surveys/${surveyId}/responses?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`
      ),
    {
      enabled: Boolean(surveyId) && enabled,
      refetchOnWindowFocus: false,
      select: (response) => ({
        responses: response.data || [],
        pagination: response.pagination || { total: 0, page: 1, totalPages: 1 },
      }),
    }
  );

export const usePublicSurvey = (slug, company, token) =>
  useQuery(
    ["public-survey", slug, company, token],
    () => fetchPublicSurvey(slug, company, token),
    {
      enabled: Boolean(slug && company),
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
      select: (response) => response.data || null,
    }
  );

export const usePublicSurveySession = () =>
  useMutation(({ slug, company, payload }) => startPublicSurveySession(slug, company, payload));

export const useSubmitPublicSurveyAnswers = () =>
  useMutation(({ slug, company, token, answers }) =>
    submitPublicSurveyAnswers(slug, company, token, answers)
  );
